/**
 * GridCrash — Replay Playback Engine
 *
 * Re-drives a fresh GridCrashEngine from (difficulty, mode, seed) and
 * dispatches a recorded {t, a, x, y} input stream on an accumulated
 * clock, so playback survives tab throttling and supports speed
 * changes and scrubbing. Mine placement depends only on the seed and
 * the first revealed cell, so a seeded stream reproduces the exact
 * board (proven by the dev __gridclashReplayCheck validator).
 *
 * Pure simulation — rendering is replay_view.ts's job via onUpdate.
 */

import {
  GridCrashEngine,
  type GameState,
  type DifficultyConfig,
  type GenerationMode,
} from "./engine.js";
import type { ReplayEvent } from "./replay.js";

export class ReplayPlayer {
  private engine: GridCrashEngine;
  private playhead = 0;      // ms into the recording
  private nextIdx = 0;       // next event to dispatch
  private speed = 1;
  private playing = false;
  private rafId: number | null = null;
  private lastTs: number | null = null;

  /** Recording length in ms (t of the final event). */
  readonly duration: number;

  onUpdate: ((state: Readonly<GameState>, playheadMs: number) => void) | null = null;
  onEnded: (() => void) | null = null;

  constructor(
    private readonly difficulty: DifficultyConfig,
    private readonly mode: GenerationMode,
    private readonly seed: number,
    private readonly events: ReplayEvent[]
  ) {
    this.duration = events.length > 0 ? events[events.length - 1].t : 0;
    this.engine = new GridCrashEngine(difficulty, mode, seed);
  }

  getState(): Readonly<GameState> {
    return this.engine.getState();
  }

  getPlayhead(): number {
    return this.playhead;
  }

  getSpeed(): number {
    return this.speed;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  isFinished(): boolean {
    return this.nextIdx >= this.events.length;
  }

  play(): void {
    if (this.playing) return;
    if (this.isFinished()) this.restart();
    this.playing = true;
    this.lastTs = null;
    this.rafId = requestAnimationFrame(this.tick);
  }

  pause(): void {
    this.playing = false;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  setSpeed(speed: number): void {
    this.speed = speed;
  }

  restart(): void {
    this.pause();
    this.engine = new GridCrashEngine(this.difficulty, this.mode, this.seed);
    this.playhead = 0;
    this.nextIdx = 0;
    this.emit();
  }

  /**
   * Jumps to a time offset. Backwards seeks rebuild the engine and
   * re-simulate — the stream is ≤2500 events, so this is instant.
   */
  seekTo(ms: number): void {
    const target = Math.max(0, Math.min(ms, this.duration));
    if (target < this.playhead) {
      this.engine = new GridCrashEngine(this.difficulty, this.mode, this.seed);
      this.nextIdx = 0;
    }
    this.playhead = target;
    this.applyDueEvents();
    this.emit();
  }

  /** Stops the loop for good. Call when the view closes. */
  destroy(): void {
    this.pause();
    this.onUpdate = null;
    this.onEnded = null;
  }

  // ─── Internals ────────────────────────────────────────────────────────────────

  private tick = (ts: number): void => {
    if (!this.playing) return;
    if (this.lastTs !== null) {
      this.playhead += (ts - this.lastTs) * this.speed;
    }
    this.lastTs = ts;

    this.applyDueEvents();
    this.emit();

    if (this.isFinished()) {
      this.playhead = this.duration;
      this.playing = false;
      this.rafId = null;
      this.onEnded?.();
      return;
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  private applyDueEvents(): void {
    while (this.nextIdx < this.events.length && this.events[this.nextIdx].t <= this.playhead) {
      const state = this.engine.getState();
      if (state.phase === "WIN" || state.phase === "LOSE") {
        this.nextIdx = this.events.length;
        return;
      }
      const ev = this.events[this.nextIdx++];
      if (ev.a === "r") this.engine.revealCell(ev.y, ev.x);
      else this.engine.toggleFlag(ev.y, ev.x);
    }
  }

  private emit(): void {
    this.onUpdate?.(this.engine.getState(), Math.min(this.playhead, this.duration));
  }
}
